import { useEffect } from "react";
import Form from "react-bootstrap/Form";
import { useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { Constants, emailPattern } from "../../../constants";
import useLogin from "../../../hooks/useLogin";
import { RouteConstants as Routes } from "../../../constants";
import useResponsive from "../../../hooks/useResponsive";
import { LoginFailureReason } from "../../../models/results/login-result.interface";
import LocalStorageUtil from "../../../utils/localStorage";
import { Button, FormWarning, Loader } from "@shltelemedicine/shl-ui-lib";
import TextFormInput from "../../Shared/TextInput/TextFormInput";
import styles from "./LoginForm.module.css";
import { ILink } from "../../../models/users.interface";
import { NavLink } from "react-router-dom";

type LoginFormInputs = { email: string; password: string };

const LoginForm = () => {
  const {
    handleSubmit,
    control,
    formState: { errors: formErrors },
    setValue,
  } = useForm<LoginFormInputs>();

  const forgotPasswordLink: ILink = {
    id: Routes.FORGOT_PASSWORD,
    to: Routes.FORGOT_PASSWORD_PATH as string,
  };

  const { loading, login, loginFailureReason } = useLogin();

  const { isTabletPortrait } = useResponsive();

  useEffect(() => {
    const userName = LocalStorageUtil.get(Constants.USERNAME_KEY);

    setValue("email", userName ?? "", { shouldValidate: false });
  }, [setValue]);

  const onSubmit = async ({ email, password }: LoginFormInputs) => {
    await login(email, password);
  };

  const loginFailureMsg = getLoginFailureMsg(loginFailureReason);

  return (
    <Form
      className="z-index-front font-md align-self-center"
      onSubmit={handleSubmit(onSubmit)}
    >
      {loginFailureReason && (
        <FormWarning className="mb-3" message={loginFailureMsg} />
      )}
      {loading && (
        <div className="p-sm-2 mb-sm-3">
          <Loader />
        </div>
      )}
      <div className="mb-3">
        <div className="d-md-flex align-items-center">
          <Form.Label
            className={`${styles.label} app-form-label mb-0 mx-0 d-block`}
          >
            <FormattedMessage id="LOGIN.USERNAME" defaultMessage="Username" />:
          </Form.Label>

          <TextFormInput
            name={"email"}
            control={control}
            rules={{
              required: true,
              pattern: emailPattern,
            }}
            input={{
              className: styles.input,
              isInvalid: !!formErrors?.email,
            }}
          />
        </div>
      </div>
      <div className="mb-2">
        <div className="d-md-flex align-items-sm-center">
          <div className="d-md-flex align-items-center">
            <Form.Label
              className={`${styles.label} app-form-label mb-0 mx-0 d-sm-block`}
            >
              <FormattedMessage id="LOGIN.PASSWORD" defaultMessage="Password" />
              :
            </Form.Label>
            <TextFormInput
              name={"password"}
              control={control}
              rules={{
                required: true,
              }}
              input={{
                className: styles.input,
                type: "password",
                isInvalid: !!formErrors?.password,
              }}
            />
          </div>
          {!isTabletPortrait && (
            <Button
              type="submit"
              bgColor="#435FC5"
              className="px-sm-4 mt-sm-0 mt-3"
            >
              LOGIN
            </Button>
          )}
        </div>
      </div>

      {isTabletPortrait && (
        <div className="d-flex justify-content-center my-1">
          <Button type="submit" bgColor="#435FC5" className="m-0">
            LOGIN
          </Button>
        </div>
      )}
      <div
        className={`d-flex mt-3 mt-sm-0 justify-content-center text-nowrap pr-4`}
      >
        <NavLink
          key={forgotPasswordLink.to}
          to={forgotPasswordLink.to}
          onClick={forgotPasswordLink.onCLick}
          className={`${styles.forgotPasswordLink}`}
        >
          <FormattedMessage
            id="LOGIN.FORGOT_PASSWORD"
            defaultMessage="Forgot your password? Click here to reset it"
          />
        </NavLink>
      </div>
    </Form>
  );
};

export default LoginForm;

const getLoginFailureMsg = (
  loginFailureReason: LoginFailureReason | null
): JSX.Element => {
  if (!loginFailureReason) return <></>;

  switch (loginFailureReason) {
    case LoginFailureReason.InvalidCredentials:
      return (
        <FormattedMessage
          id="LOGIN.INVALID_CREDENTIALS"
          defaultMessage="Invalid User Name or Password.<br></br>Please check your credentials and try again"
          values={{
            br: () => <br></br>,
          }}
        />
      );
    case LoginFailureReason.Blocked:
      return (
        <FormattedMessage
          id="LOGIN.BLOCKED"
          defaultMessage="You are not authorized to access this portal.<br></br>Contact your system administrator."
          values={{
            br: () => <br></br>,
          }}
        />
      );
    case LoginFailureReason.Unknown:
    default:
      return (
        <FormattedMessage
          id="LOGIN.FAILURE"
          defaultMessage="Username or Password are incorrect"
        />
      );
  }
};
